// src/pages/Home.jsx
import React from "react";
import HeroCarousel from "../components/Hero/HeroCarousel";
import TrustStrip from "../components/sections/TrustStrip";
import Services from "../components/sections/Services";
import BeforeAfterResults from "../components/sections/BeforeAfterResults";
import WhyUsPreview from "../components/sections/WhyUsPreview";
import HotspotDetection from "../components/sections/HotspotDetection";
import Stats from "../components/sections/Stats";
import Areas from "../components/sections/Areas";
import Testimonials from "../components/sections/Testimonials";
import PackagesPreview from "../components/sections/PackagesPreview";
import FAQ from "../components/sections/FAQ";
import CTA from "../components/sections/CTA";
import Reveal from "../components/Reveal";

import Meta from '../Meta'
import { copy } from '../lib/copy'
import { useSiteSettings } from '../lib/useSiteSettings'
import { useCms } from '../lib/useCms'

// A URL literally naming the old pre-rebrand business ("arcturus_services"
// etc.) can't be Horizon's own profile regardless of who entered it. The
// actual root cause - lib/config.js and seedContent.js defaulting new
// installs to real arcturus URLs, which is what put them in the live
// production database in the first place - has since been fixed at the
// source, and the one row that had already been seeded with them was
// corrected directly. This pattern match stays on as cheap defense-in-
// depth against the same mistake recurring, filtered out of the
// structured-data claim specifically, without touching Footer's plain
// social icons (which are just links, not an "this represents Horizon"
// assertion) or deleting the CMS fields themselves. Once real accounts are
// added in Admin > Website Settings, they flow through automatically.
const STALE_BRAND_PATTERN = /arcturus/i;
const validSocialUrls = (socials) =>
  Object.values(socials || {}).filter((url) => url && !STALE_BRAND_PATTERN.test(url));

export default function Home(){
  const areaNames = (copy.areas?.featured || []).map((a) => a.name);
  const biz = useSiteSettings();
  // Mirrors FAQ.jsx's own rule exactly: real CMS FAQs when they exist,
  // never a mix with the static fallback copy - matches exactly what the
  // visible FAQ section below renders, since it's back on the homepage
  // again (also still present on /why-us).
  const { data: apiFaqs } = useCms("/api/faqs", null);
  const faqItems = apiFaqs?.length ? apiFaqs.map((f) => ({ q: f.question, a: f.answer })) : copy.faq.items;
  const sameAs = validSocialUrls(biz.socials);
  // Real, verified Google Business Profile figures only - see the
  // aggregateRating usage below for why this must never be derived from
  // anything else.
  const googleRating = Number(biz.googleRating);
  const googleReviewCount = Number(biz.googleReviewCount);
  const canUseAggregateRating =
    Number.isFinite(googleRating) && googleRating > 0 && googleRating <= 5 &&
    Number.isInteger(googleReviewCount) && googleReviewCount > 0;

  return (
    <>
      <Meta
        title={`Solar Panel Cleaning & Thermal Inspection Sydney | ${biz.name}`}
        desc={`Professional solar panel cleaning combined with DJI Matrice 4T thermal drone inspection, plus roof and concrete cleaning across Sydney. Same-day service. Call ${biz.phone}.`}
        path="/"
      >
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HomeAndConstructionBusiness",
          "@id": "https://horizonsolar.com.au/#business",
          name: biz.name,
          url: "https://horizonsolar.com.au",
          image: "https://horizonsolar.com.au/images/drone-sequence/scene-1-front.webp",
          telephone: biz.phone,
          email: biz.email,
          priceRange: "$$",
          description: "Solar panel cleaning, DJI Matrice 4T thermal drone inspection, roof cleaning and concrete cleaning across Sydney, NSW.",
          address: {
            "@type": "PostalAddress",
            addressLocality: "Sydney",
            addressRegion: "NSW",
            addressCountry: "AU",
          },
          geo: { "@type": "GeoCoordinates", latitude: -33.8688, longitude: 151.2093 },
          areaServed: areaNames.map((name) => ({ "@type": "City", name })),
          openingHoursSpecification: {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
            opens: "08:00",
            closes: "18:00",
          },
          ...(sameAs.length ? { sameAs } : {}),
          // AggregateRating is a real, distinct dataset from the curated
          // Reviews table (a handful of hand-picked testimonial cards) -
          // it must only ever reflect Horizon's actual Google Business
          // Profile numbers, entered by an admin who can see the real
          // profile, never derived from testimonial count or estimated.
          // Omitted entirely (not zero-filled) until both a real rating
          // and a real review count are configured - Schema.org/Google
          // guidance is explicit that omitting the field is preferable to
          // a number that can't be backed up.
          ...(canUseAggregateRating ? {
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: String(googleRating),
              reviewCount: String(googleReviewCount),
            },
          } : {}),
        })}</script>

        {/* FAQPage - real CMS questions (faqItems above), matching exactly
            what the visible FAQ section below renders - never the static
            fallback copy once real ones exist. */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqItems.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        })}</script>
      </Meta>

    <main className="relative bg-[#02060c]">
      {/* Hero Section - 3-slide carousel: main + lead form, solar before/after,
          thermal inspection + DJI Matrice 4T */}
      <HeroCarousel />
      {/* Marker the Header watches to know when the hero has scrolled past,
          so the floating nav can switch from transparent-over-photo to its
          normal solid shape once there's dark page content behind it
          instead of the hero image. */}
      <div id="hero-end" />

      {/* Key differentiators, directly under the hero */}
      <TrustStrip />

      {/* Services Offered - solar first, roof second, concrete third */}
      <Reveal><Services /></Reveal>

      {/* Before/after results - solar, roof and concrete transformations */}
      <Reveal><BeforeAfterResults /></Reveal>

      {/* Compact Why Us preview - full walkthrough now lives at /why-us */}
      <Reveal><WhyUsPreview /></Reveal>

      {/* Thermal fault detection - the drone/thermal technology highlight;
          the fuller educational companion section now lives at /why-us
          alongside it, rather than duplicating similar content here too. */}
      <Reveal><HotspotDetection /></Reveal>

      {/* Trust stats - real, existing claims in a huge-number strip */}
      <Reveal><Stats /></Reveal>

      {/* Service area coverage */}
      <Reveal><Areas /></Reveal>

      {/* Customer Testimonials - reviews / trust signals */}
      <Reveal><Testimonials /></Reveal>

      {/* Compact packages CTA - full catalogue now lives at /packages */}
      <Reveal><PackagesPreview /></Reveal>

      {/* FAQ Section */}
      <Reveal><FAQ /></Reveal>

      {/* Final Call To Action */}
      <Reveal><CTA /></Reveal>
    </main>

    </>
  )
}
